// Credits Service - Firestore-based credit system
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    addDoc,
    collection,
    query,
    orderBy,
    limit,
    getDocs,
    increment,
    serverTimestamp,
    runTransaction
} from 'firebase/firestore';
import { db } from './config.js';

/**
 * Credit packages available for purchase
 */
export const CREDIT_PACKAGES = [
    { id: 'credits_1',  label: '$1',  price: 1,  credits: 1  },
    { id: 'credits_3',  label: '$3',  price: 3,  credits: 3  },
    { id: 'credits_5',  label: '$5',  price: 5,  credits: 5  },
    { id: 'credits_10', label: '$10', price: 10, credits: 10 },
    { id: 'credits_15', label: '$15', price: 15, credits: 15 },
    { id: 'credits_20', label: '$20', price: 20, credits: 20 },
    { id: 'credits_50', label: '$50', price: 50, credits: 50 },
];

/** Cost in credits per assembly operation */
export const ASSEMBLY_CREDIT_COST = 1;

/**
 * Get or create the user's profile document (with credits)
 * @param {string} uid
 * @returns {Promise<{credits: number, totalSpent: number}>}
 */
export async function getUserProfile(uid) {
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
        // First time — create profile with 0 credits
        const profile = { credits: 0, totalSpent: 0, createdAt: serverTimestamp() };
        await setDoc(ref, profile);
        return { credits: 0, totalSpent: 0 };
    }
    return snap.data();
}

/**
 * Get the current credit balance for a user
 * @param {string} uid
 * @returns {Promise<number>}
 */
export async function getUserCredits(uid) {
    const profile = await getUserProfile(uid);
    return profile.credits ?? 0;
}

/**
 * Add credits to a user's account (after a successful purchase)
 * @param {string} uid
 * @param {number} amount - number of credits to add
 * @param {string} skuId - Google Play SKU ID
 * @param {string} orderId - Google Play order ID
 * @returns {Promise<number>} new balance
 */
export async function addCredits(uid, amount, skuId, orderId = '') {
    const userRef = doc(db, 'users', uid);
    const txRef = collection(db, 'users', uid, 'transactions');

    let newBalance;

    await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(userRef);
        const current = snap.exists() ? (snap.data().credits ?? 0) : 0;
        newBalance = current + amount;

        if (snap.exists()) {
            transaction.update(userRef, {
                credits: newBalance,
                updatedAt: serverTimestamp()
            });
        } else {
            transaction.set(userRef, {
                credits: newBalance,
                totalSpent: 0,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        }
    });

    // Log transaction separately (not inside the Firestore transaction to avoid read-after-write issues)
    await addDoc(txRef, {
        type: 'purchase',
        amount: +amount,
        skuId,
        orderId,
        description: `Purchased ${amount} credit${amount !== 1 ? 's' : ''} ($${amount})`,
        createdAt: serverTimestamp()
    });

    return newBalance;
}

/**
 * Deduct credits for an assembly operation (atomic)
 * @param {string} uid
 * @param {number} cost - defaults to ASSEMBLY_CREDIT_COST
 * @param {string} description
 * @returns {Promise<{success: boolean, balance: number, error?: string}>}
 */
export async function deductCredits(uid, cost = ASSEMBLY_CREDIT_COST, description = 'Assembly operation') {
    const userRef = doc(db, 'users', uid);
    const txRef = collection(db, 'users', uid, 'transactions');

    let newBalance;
    let success = false;
    let errorMsg;

    try {
        await runTransaction(db, async (transaction) => {
            const snap = await transaction.get(userRef);
            const current = snap.exists() ? (snap.data().credits ?? 0) : 0;

            if (current < cost) {
                throw new Error('INSUFFICIENT_CREDITS');
            }

            newBalance = current - cost;

            if (snap.exists()) {
                transaction.update(userRef, {
                    credits: newBalance,
                    totalSpent: increment(cost),
                    updatedAt: serverTimestamp()
                });
            } else {
                throw new Error('INSUFFICIENT_CREDITS');
            }
        });

        success = true;

        await addDoc(txRef, {
            type: 'deduction',
            amount: -cost,
            description,
            createdAt: serverTimestamp()
        });
    } catch (e) {
        if (e.message === 'INSUFFICIENT_CREDITS') {
            errorMsg = 'INSUFFICIENT_CREDITS';
        } else {
            throw e;
        }
    }

    return { success, balance: newBalance ?? 0, error: errorMsg };
}

/**
 * Get recent transaction history
 * @param {string} uid
 * @param {number} limitCount
 * @returns {Promise<Array>}
 */
export async function getTransactionHistory(uid, limitCount = 20) {
    const q = query(
        collection(db, 'users', uid, 'transactions'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
